import axios from "axios";
import PropTypes from "prop-types";
import { useEffect, useReducer, useCallback, useMemo } from "react";

// locales
import { useLocales } from "src/locales";

import {
  me,
  login as verify,
  logout as logoutEndpoint,
  loginEnterprise as verifyEnterprise,
  loginMfa as verifyMfa,
  loginSocialNetwork as verifySocialNetwork,
  resetPassword as resetPasswordEndpoint,
} from "src/api/user";

import { getMessagingDeviceToken } from "src/utils/fcm-token";

// redux
import { dispatch as dispatchRedux } from "../../../redux/store";
import { setAuthenticated } from "../../../redux/slices/userProvider";

// utils
import { STATUS_CODES } from "../../../utils/axios";
import { localStorageAvailable } from "../../../utils/storage-available";
import {
  setSession,
  expireSession,
  enterMaintenance,
  refreshToken as refreshTokenApi,
} from "../../../utils/token";

//
import { AuthContext } from "./auth-context";

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  loading: true,
  authenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      loading: false,
      authenticated: action.payload.authenticated,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGIN") {
    return {
      ...state,
      authenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === "REGISTER") {
    return {
      ...state,
      authenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGOUT") {
    return {
      ...state,
      authenticated: false,
      user: null,
    };
  }

  return state;
};

/*
  Is a failed me() the backend's fault rather than the session's?

  The rejection reaches us in two shapes: the raw axios error, for a request that never got an
  answer (the response interceptor's default branch), or the response body alone, because the
  interceptor rejects 400/401/500 with error.response.data.

  The rule is token.js's: only a request that came back with no answer, with a 5xx or with a
  timeout is an outage. Anything else -- including a shape we cannot read -- counts as a real
  authentication failure, so a session that is genuinely dead is never kept alive.
*/
const isTransientMeError = error => {
  if (error?.request) {
    return (
      !error.response ||
      error.response.status >= STATUS_CODES.serverError ||
      error.response.data?.type === "Error.Timeout"
    );
  }

  return error?.status >= STATUS_CODES.serverError || error?.type === "Error.Timeout";
};

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const storageAvailable = localStorageAvailable();
  const { loadNamespaces, onChangeLang } = useLocales();

  const refresh = useCallback(
    async (isInitializing = true) => {
      const responseResult = await refreshTokenApi(false, isInitializing);

      if (
        responseResult.result === "expiredToken" ||
        responseResult.result === "error" ||
        responseResult.result === "notAuthenticated"
      ) {
        // "error" means a transient backend problem: token.js has kept the session and
        // routed the user to the maintenance page. We still stop loading so the app
        // renders (the maintenance route is public) instead of hanging on the splash.
        dispatch({
          type: "INITIAL",
          payload: {
            authenticated: false,
            user: null,
          },
        });
      } else {
        let user = responseResult.data?.user;
        // Only a confirmed authentication failure may clear the credentials we just refreshed.
        let keepSession = false;

        // When reusing an access token user data must be refetched
        if (!user) {
          try {
            user = await me();
          } catch (error) {
            if (axios.isCancel(error)) {
              // The request interceptor aborted the call because token.js had already resolved
              // this session: it either expired it and sent the user to login, or kept it and
              // parked them on maintenance. The session is not ours to tear down -- doing it
              // here would also clear the login-problem markers the retry loop needs.
              keepSession = true;
            } else if (isTransientMeError(error)) {
              // The backend is unhealthy, not the session. Keep the refreshed tokens usable and
              // route the user through the same maintenance retry a failed refresh uses.
              enterMaintenance();
              keepSession = true;
            }

            // Anything else is a confirmed authentication failure -- a 401, or a 400 whose
            // specific reason the response interceptor already surfaced (user.enterpriseNotFound
            // / user.noEnterpriseAvailable). keepSession stays false so it is torn down below.

            user = null;
          }
        }

        if (!user) {
          // Tear the session down rather than leaving the access token in localStorage while the
          // app renders as logged out: the next request would succeed on that stale token. When
          // the profile load failed for a reason that is not the session's fault, the tokens are
          // still good and whoever kept them owns the recovery, so they stay put.
          if (!keepSession) expireSession();

          dispatch({
            type: "INITIAL",
            payload: {
              authenticated: false,
              user: null,
            },
          });

          return;
        }

        onChangeLang(user.language);
        await loadNamespaces("label");

        dispatch({
          type: "INITIAL",
          payload: {
            authenticated: true,
            user,
          },
        });
      }
    },
    [loadNamespaces, onChangeLang]
  );

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem("accessToken") : "";

      if (accessToken) {
        await refresh(true);
      } else {
        dispatch({
          type: "INITIAL",
          payload: {
            authenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: "INITIAL",
        payload: {
          authenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable, refresh]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    dispatchRedux(
      setAuthenticated({
        authenticated: state.authenticated,
        user: state.user,
      })
    );
  }, [state.authenticated, state.user]);

  // LOGIN
  const login = useCallback(
    async (email, password, enterpriseId, rememberMe) => {
      // Rejects on any non 2xx (these calls bypass the axios interceptors), so the caller's
      // catch is what reports a failed login.
      const response = await verify({
        email,
        password,
        enterpriseId,
        rememberMe,
      });

      const { accessToken, refreshToken, user, enterprises, requiresMfa, mfaOptions, challengeId } =
        response.data;

      if (enterprises.length === 0 && !requiresMfa) {
        setSession(accessToken, refreshToken, refreshToken !== null);

        onChangeLang(user.language);
        await loadNamespaces("label");

        dispatch({
          type: "LOGIN",
          payload: {
            user,
          },
        });
      }

      return { enterprises, user, requiresMfa, mfaOptions, challengeId };
    },
    [loadNamespaces, onChangeLang]
  );

  // LOGIN ENTERPRISE
  const loginEnterprise = useCallback(
    async (challengeId, enterpriseId) => {
      const response = await verifyEnterprise({
        challengeId,
        enterpriseId,
      });

      const {
        accessToken,
        refreshToken,
        user,
        requiresMfa,
        mfaOptions,
        challengeId: newChallengeId,
      } = response.data;

      if (!requiresMfa) {
        setSession(accessToken, refreshToken, refreshToken !== null);

        onChangeLang(user.language);
        await loadNamespaces("label");

        dispatch({
          type: "LOGIN",
          payload: {
            user,
          },
        });
      }

      return { user, requiresMfa, mfaOptions, challengeId: newChallengeId };
    },
    [loadNamespaces, onChangeLang]
  );

  // LOGIN MFA
  const loginMfa = useCallback(
    async (challengeId, otp) => {
      const response = await verifyMfa({
        challengeId,
        otp,
      });

      const { accessToken, refreshToken, user } = response.data;

      setSession(accessToken, refreshToken, refreshToken !== null);

      onChangeLang(user.language);
      await loadNamespaces("label");

      dispatch({
        type: "LOGIN",
        payload: {
          user,
        },
      });

      return { user };
    },
    [loadNamespaces, onChangeLang]
  );

  const loginSocialNetwork = useCallback(
    async (token, authorizationCode, socialNetwork, rememberMe) => {
      const response = await verifySocialNetwork({
        token,
        authorizationCode,
        socialNetwork,
        rememberMe,
      });

      const { accessToken, refreshToken, challengeId, user, enterprises } = response.data;

      if (enterprises.length === 0) {
        setSession(accessToken, refreshToken, rememberMe);

        onChangeLang(user.language);
        await loadNamespaces("label");

        dispatch({
          type: "LOGIN",
          payload: {
            user,
          },
        });
      }

      return { enterprises, user, challengeId };
    },
    [loadNamespaces, onChangeLang]
  );

  // RESET PASSWORD
  /*
    Changing the password ends every session on the backend, this one included, so the response
    carries a new pair of tokens to continue on. A refresh token is only asked for when the session
    already holds one: a session that was not remembered must not become one.
  */
  const resetPassword = useCallback(async (challengeId, oldPassword, newPassword) => {
    const rememberMe = Boolean(localStorage.getItem("refreshToken"));

    const { accessToken, refreshToken, user } = await resetPasswordEndpoint({
      challengeId,
      oldPassword,
      newPassword,
      rememberMe,
    });

    setSession(accessToken, refreshToken, Boolean(refreshToken));

    dispatch({
      type: "LOGIN",
      payload: {
        user,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    // const response = await axios.post('/api/account/register', {
    //   email,
    //   password,
    //   firstName,
    //   lastName,
    // });
    // const { accessToken, user } = response.data;
    // localStorage.setItem('accessToken', accessToken);
    // dispatch({
    //   type: 'REGISTER',
    //   payload: {
    //     user,
    //   },
    // });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    if (storageAvailable && localStorage.getItem("accessToken")) {
      try {
        // Read the FCM token BEFORE any teardown: expireSession() revokes it below and the backend
        // needs its value to delete the matching user_device_token row. Resolves to null when push
        // is unsupported or the permission was never granted, and never prompts.
        const deviceToken = await getMessagingDeviceToken();

        await logoutEndpoint({
          refreshToken: localStorage.getItem("refreshToken"),
          deviceToken,
        });
      } catch (e) {}
    }

    // expireSession() owns the FCM revoke now. Do not re-add an explicit
    // deleteMessagingDeviceToken() here: before the read it destroys the value the endpoint needs,
    // after it, it just deletes twice.
    expireSession();
    dispatch({
      type: "LOGOUT",
    });
  }, [storageAvailable]);
  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

  const status = state.loading ? "loading" : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      authenticated: state.authenticated,
      user: state.user,
      method: "jwt",
      loading: status === "loading",
      //
      login,
      loginEnterprise,
      loginMfa,
      register,
      loginSocialNetwork,
      logout,
      refresh,
      resetPassword,
    }),
    [
      state.authenticated,
      state.user,
      login,
      loginEnterprise,
      loginMfa,
      logout,
      loginSocialNetwork,
      register,
      refresh,
      resetPassword,
      status,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
